import React from 'react';
import { Search, X, Tag, Filter } from 'lucide-react';

interface MediaHistorySearchBarProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  selectedTag: string;
  onTagSelect: (tag: string) => void;
  availableTags: string[];
  selectedType: 'all' | 'video' | 'image' | 'short';
  onTypeSelect: (type: 'all' | 'video' | 'image' | 'short') => void;
  totalCount: number;
  filteredCount: number;
  compact?: boolean;
}

export const MediaHistorySearchBar: React.FC<MediaHistorySearchBarProps> = ({
  searchQuery,
  onSearchChange,
  selectedTag,
  onTagSelect,
  availableTags,
  selectedType,
  onTypeSelect,
  totalCount,
  filteredCount,
  compact = false
}) => {
  const hasActiveFilters = searchQuery.trim() !== '' || (selectedTag !== '' && selectedTag !== 'all') || selectedType !== 'all';

  const handleClear = () => {
    onSearchChange('');
    onTagSelect('all');
    onTypeSelect('all');
  };

  return (
    <div
      id="media-history-search-bar"
      className={`bg-white border border-slate-200/90 rounded-2xl shadow-2xs ${
        compact ? 'p-2.5 space-y-2' : 'p-4 space-y-3'
      }`}
    >
      {/* Search Input Field */}
      <div className="relative flex items-center">
        <Search className="w-4 h-4 absolute left-3 text-slate-400 pointer-events-none" />
        <input
          id="history-search-input"
          type="text"
          value={searchQuery}
          onChange={e => onSearchChange(e.target.value)}
          placeholder="Search media by title or tags (e.g. vlog, sunset, #tech)..."
          className={`w-full pl-9 pr-8 text-xs sm:text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:bg-white focus:border-blue-900 focus:ring-1 focus:ring-blue-900/30 transition-all text-slate-900 placeholder:text-slate-400 ${
            compact ? 'py-1.5' : 'py-2'
          }`}
        />
        {searchQuery && (
          <button
            id="clear-search-query-btn"
            onClick={() => onSearchChange('')}
            className="absolute right-2.5 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
            title="Clear search text"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Media Type Filter Tabs */}
      <div className="flex items-center justify-between gap-2 overflow-x-auto no-scrollbar pt-0.5">
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[11px] font-medium text-slate-500 hidden sm:inline-flex items-center gap-1">
            <Filter className="w-3 h-3 text-slate-400" />
            Type:
          </span>
          {[
            { id: 'all', label: 'All Media' },
            { id: 'video', label: 'Videos' },
            { id: 'short', label: 'Shorts' },
            { id: 'image', label: 'Images' }
          ].map(type => (
            <button
              key={type.id}
              id={`filter-type-${type.id}`}
              onClick={() => onTypeSelect(type.id as any)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                selectedType === type.id
                  ? 'bg-slate-900 text-white shadow-2xs'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {type.label}
            </button>
          ))}
        </div>

        {/* Results Counter & Reset */}
        <div className="flex items-center gap-2 shrink-0 text-[11px] text-slate-500 font-mono">
          <span>
            {filteredCount} / {totalCount} items
          </span>
          {hasActiveFilters && (
            <button
              id="reset-history-filters-btn"
              onClick={handleClear}
              className="text-[10px] font-sans font-semibold text-rose-600 hover:text-rose-700 underline cursor-pointer"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {/* Tag Filtering Chips */}
      {availableTags.length > 0 && (
        <div className="pt-1 border-t border-slate-100 flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1">
          <div className="flex items-center gap-1 text-[10px] uppercase font-semibold text-slate-400 tracking-wider shrink-0 mr-1">
            <Tag className="w-3 h-3 text-slate-400" />
            <span>Tags:</span>
          </div>

          <button
            id="tag-pill-all"
            onClick={() => onTagSelect('all')}
            className={`px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 transition-colors cursor-pointer ${
              selectedTag === 'all' || !selectedTag
                ? 'bg-blue-900 text-white font-semibold shadow-2xs'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            All
          </button>

          {availableTags.map(tag => {
            const isSelected = selectedTag.toLowerCase() === tag.toLowerCase();
            return (
              <button
                key={tag}
                id={`tag-pill-${tag}`}
                onClick={() => onTagSelect(isSelected ? 'all' : tag)}
                className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium shrink-0 transition-colors cursor-pointer ${
                  isSelected
                    ? 'bg-emerald-600 text-white font-semibold shadow-2xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900'
                }`}
              >
                #{tag}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
